import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: "",
  date: "",
  time_label: "18:00 Uhr",
  duration: "60 Min",
  speaker: "",
  highlightsText: "",
  recording_url: "",
  slides_url: "",
};

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "h-11 rounded-xl bg-cream-50 border-cream-300 text-sand-800 focus-visible:ring-terra";

/** Admin-Dialog zum Anlegen/Bearbeiten eines Briefings. `briefing` = null → neu. */
export default function BriefingFormDialog({ briefing, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(briefing
        ? {
            title: briefing.title,
            date: briefing.date,
            time_label: briefing.time_label || "18:00 Uhr",
            duration: briefing.duration || "60 Min",
            speaker: briefing.speaker,
            highlightsText: (briefing.highlights || []).join("\n"),
            recording_url: briefing.recording_url || "",
            slides_url: briefing.slides_url || "",
          }
        : EMPTY);
    }
  }, [open, briefing]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      date: form.date,
      time_label: form.time_label.trim() || "18:00 Uhr",
      duration: form.duration.trim() || "60 Min",
      speaker: form.speaker.trim(),
      highlights: form.highlightsText.split("\n").map((h) => h.trim()).filter(Boolean),
      recording_url: form.recording_url.trim() || null,
      slides_url: form.slides_url.trim() || null,
    };
    try {
      if (briefing) {
        await axios.put(`${API}/briefings/${briefing.id}`, payload, { withCredentials: true });
        toast.success("Briefing aktualisiert.");
      } else {
        await axios.post(`${API}/briefings`, payload, { withCredentials: true });
        toast.success("Briefing angelegt.");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent data-testid="briefing-form-dialog" className="max-w-xl max-h-[92vh] overflow-y-auto bg-white border-cream-300 rounded-3xl">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-2xl font-normal tracking-tight text-sand-800">
            {briefing ? "Briefing bearbeiten" : "Neues Briefing anlegen"}
          </DialogTitle>
          <DialogDescription className="text-sand-600 text-sm">
            Erscheint sofort im Dashboard und auf der Briefings-Seite aller Mitglieder.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <Field id="bf-title" label="Titel *">
            <Input id="bf-title" data-testid="briefing-form-title" className={inputCls} required minLength={4}
              placeholder="MD-Prüfung 2026 · Neue Schwerpunkte" value={form.title} onChange={set("title")} />
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field id="bf-date" label="Datum *">
              <Input id="bf-date" data-testid="briefing-form-date" className={inputCls} type="date" required
                value={form.date} onChange={set("date")} />
            </Field>
            <Field id="bf-time" label="Uhrzeit">
              <Input id="bf-time" className={inputCls} placeholder="18:00 Uhr"
                value={form.time_label} onChange={set("time_label")} />
            </Field>
            <Field id="bf-duration" label="Dauer">
              <Input id="bf-duration" className={inputCls} placeholder="60 Min"
                value={form.duration} onChange={set("duration")} />
            </Field>
          </div>

          <Field id="bf-speaker" label="Referent:innen *">
            <Input id="bf-speaker" data-testid="briefing-form-speaker" className={inputCls} required minLength={2}
              placeholder="Dickie & RA Stefanie Köhler" value={form.speaker} onChange={set("speaker")} />
          </Field>

          <Field id="bf-highlights" label="Themen (eine Zeile pro Punkt)">
            <Textarea id="bf-highlights" rows={3}
              className="rounded-xl bg-cream-50 border-cream-300 text-sand-800 focus-visible:ring-terra"
              placeholder={"Neue Prüfschwerpunkte\nQ&A aus der Community"}
              value={form.highlightsText} onChange={set("highlightsText")} />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="bf-rec" label="Aufzeichnungs-URL (Archiv)">
              <Input id="bf-rec" className={inputCls} type="url" placeholder="https://…"
                value={form.recording_url} onChange={set("recording_url")} />
            </Field>
            <Field id="bf-slides" label="Slides-URL (PDF)">
              <Input id="bf-slides" className={inputCls} type="url" placeholder="https://…"
                value={form.slides_url} onChange={set("slides_url")} />
            </Field>
          </div>

          <Button type="submit" disabled={saving} data-testid="briefing-form-submit"
            className="w-full rounded-full bg-terra hover:bg-terra-dark text-white py-3 font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : briefing ? "Änderungen speichern" : "Briefing anlegen"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
